/* eslint-disable max-lines, @typescript-eslint/consistent-type-assertions */

import {
  buildWorkList,
  gcdArray,
  itemTotalCents,
  measureEffectiveStep,
  sumItensCents,
  toMoney3,
} from '../../utils';

/**
 * Representa a direção do ajuste:
 * +1 para aumentar total; -1 para reduzir total.
 */
type Direction = 1 | -1;

/**
 * Estrutura mínima esperada de um item de trabalho retornado por buildWorkList().
 * Mantemos genérico para não forçar mudanças nos utilitários já existentes.
 */
type WorkItem = {
  index: number;
  val: number; // valor unitário com 3 casas
  qtd: number; // quantidade
};

/**
 * Resultado de measureEffectiveStep() — supõe-se que retorne o delta
 * (em centavos) que um "passo efetivo" provoca e quantos passos de 0,001
 * compõem esse passo efetivo (unitSteps).
 */
type EffectiveStep = {
  deltaCents: number; // pode ser positivo (aumenta total) ou negativo (reduz total)
  unitSteps: number; // nº de passos de 0,001 que formam 1 passo efetivo
};

/**
 * Item enriquecido com metadados de passo efetivo.
 */
type Enriched = WorkItem & {
  stepCents: number; // |deltaCents| do passo efetivo, em centavos
  unitSteps: number; // k de passos 0,001 por passo efetivo
  capUnitSteps: number; // limite de passos 0,001 ao reduzir (para não ficar < 0)
};

/**
 * Motivos possíveis para não alcançar o alvo exato.
 */
type ResidualReason = 'unsolvable_granularity' | 'no_movable_items';

/**
 * Resultado do ajuste de ST.
 */
type ApplyResult<T = any> = {
  updated: T[];
  residualCents: number;
  reason?: ResidualReason;
};

/**
 * Converte qualquer valor numérico para um inteiro em centavos com segurança.
 */
const asCents = (n: number): number => (Number.isFinite(n) ? Math.trunc(n) : 0);

/**
 * Garante que um número é não-negativo.
 */
const clampNonNegative = (n: number): number => (n < 0 ? 0 : n);

/**
 * Seleciona a direção com base em diff.
 */
const getDirection = (diffCents: number): Direction => (diffCents > 0 ? 1 : -1);

/**
 * Enriquecimento com metadados de passo efetivo e capacidade de redução.
 */
const enrichWork = (work: WorkItem[], direction: Direction): Enriched[] =>
  work
    .map<Enriched | null>((w) => {
      const eff = measureEffectiveStep(
        w.val,
        w.qtd,
        direction,
      ) as EffectiveStep;
      const stepCents = Math.abs(eff.deltaCents);
      if (!stepCents) return null;

      // capacidade de reduzir (em "passos 0,001") — evita ficar negativo
      const capUnitSteps =
        direction === -1
          ? clampNonNegative(Math.floor(w.val * 1000))
          : Number.MAX_SAFE_INTEGER;

      // quando aumentando (+1) não há "cap" relevante; quando reduzindo (-1) exige cap > 0
      if (direction === -1 && capUnitSteps <= 0) return null;

      return {
        index: w.index,
        val: w.val,
        qtd: w.qtd,
        stepCents,
        unitSteps: eff.unitSteps,
        capUnitSteps,
      };
    })
    .filter(Boolean) as Enriched[];

/**
 * Planeja a quantidade de "passos efetivos" por item (rateio proporcional ao peso).
 */
const planEffectiveSteps = (
  enriched: Enriched[],
  diffCents: number,
  direction: Direction,
): number[] => {
  const weights = enriched.map((e) => itemTotalCents(e.val, e.qtd));
  const sumWeights = weights.reduce((a, b) => a + b, 0) || 1;

  return enriched.map((e, i) => {
    const quota = diffCents * (weights[i] / sumWeights); // em centavos
    const raw = quota / e.stepCents;

    // Para aumentar (+1) arredonda para baixo; para reduzir (-1) arredonda para cima
    const kEff = direction === 1 ? Math.floor(raw) : Math.ceil(raw);

    // Não permitir inversão do sentido (ex.: retornar positivo quando reduzindo)
    const bounded = direction === 1 ? Math.max(0, kEff) : Math.min(0, kEff);

    // Converte "k passos efetivos" para "passos 0,001" e confere capacidade quando reduzindo
    const unitNeeded = Math.abs(bounded) * e.unitSteps;
    if (direction === -1 && unitNeeded > e.capUnitSteps) {
      return Math.sign(bounded) * Math.floor(e.capUnitSteps / e.unitSteps);
    }

    return bounded;
  });
};

/**
 * Aplica os blocos planejados de passos efetivos por item, devolvendo novos valores e o restante.
 */
const applyPlannedBlocks = (
  enriched: Enriched[],
  plannedEffSteps: number[],
  diffCents: number,
): { updated: Enriched[]; remained: number } => {
  return enriched.reduce(
    (acc, e, idx) => {
      const kEff = plannedEffSteps[idx] || 0;
      if (kEff === 0) {
        acc.updated.push(e);
        return acc;
      }

      // total de "passos 0,001" correspondentes a k passos efetivos
      const unitStepsTotal = Math.abs(kEff) * e.unitSteps;
      const unitDelta = 0.001 * unitStepsTotal * Math.sign(kEff); // variação no unitário
      const before = itemTotalCents(e.val, e.qtd);
      const nextVal = clampNonNegative(toMoney3(e.val + unitDelta));
      const after = itemTotalCents(nextVal, e.qtd);

      acc.remained -= after - before;
      acc.updated.push({ ...e, val: nextVal });

      return acc;
    },
    { updated: [] as Enriched[], remained: diffCents },
  );
};

/**
 * Seleciona o candidato cujo passo efetivo mais reduz o gap do restante.
 */
const selectBestCandidate = (
  candidates: (Enriched & EffectiveStep & { canMove: boolean })[],
  remained: number,
): (Enriched & EffectiveStep) | null => {
  if (!candidates.length) return null;

  return candidates.reduce((best, cand) => {
    const gap = Math.abs(remained - cand.deltaCents);
    const bestGap = Math.abs(remained - best.deltaCents);
    return gap < bestGap ? cand : best;
  });
};

/**
 * Ajuste fino por passos unitários efetivos até atingir o alvo ou atingir o limite de iterações.
 * O limite é dimensionado pelo menor passo efetivo observado.
 */
const fineTuneByEffectiveSteps = (
  enriched: Enriched[],
  remainedStart: number,
  direction: Direction,
): { updated: Enriched[]; remained: number } => {
  let remained = remainedStart;
  let updated = enriched;

  // Usa passo mínimo observado apenas para dimensionar o teto de iterações
  const minStep = Math.max(1, Math.min(...updated.map((e) => e.stepCents)));
  const SAFE_CAP = 1_000_000;
  const MAX_STEPS =
    Math.min(
      SAFE_CAP,
      Math.ceil(Math.abs(remained) / minStep) + updated.length * 12,
    ) || updated.length * 12;

  for (let steps = 0; steps < MAX_STEPS && remained !== 0; steps += 1) {
    // Se passou do ponto, não continue no mesmo sentido
    if (Math.sign(remained) !== direction) break;

    // Recalcula passo efetivo no estado atual
    const candidates = updated
      .map((e) => {
        const eff = measureEffectiveStep(
          e.val,
          e.qtd,
          direction,
        ) as EffectiveStep;

        const canIncrease = direction === 1 && eff.deltaCents > 0;
        const canDecrease =
          direction === -1 &&
          eff.deltaCents < 0 &&
          e.val > 0 &&
          eff.unitSteps <= e.capUnitSteps;

        return { ...e, ...eff, canMove: canIncrease || canDecrease };
      })
      .filter((c) => c.canMove);

    const chosen = selectBestCandidate(candidates, remained);
    if (!chosen) break;

    // Aplica 1 passo efetivo (unitSteps × 0,001) no sentido correto
    const before = itemTotalCents(chosen.val, chosen.qtd);
    const unitDelta = Math.sign(direction) * chosen.unitSteps * 0.001;
    const nextVal = clampNonNegative(toMoney3(chosen.val + unitDelta));
    const after = itemTotalCents(nextVal, chosen.qtd);

    const delta = after - before; // quanto o total mudou (cents)

    // Se por algum motivo não moveu no sentido esperado, aborta
    if (direction === 1 && delta <= 0) break;
    if (direction === -1 && delta >= 0) break;

    // Se não melhora, não aplica (evita "explodir")
    if (Math.abs(remained - delta) >= Math.abs(remained)) break;

    remained -= delta;

    updated = updated.map((e) =>
      e.index === chosen.index ? { ...e, val: nextVal } : e,
    );
  }

  return { updated, remained };
};

/**
 * Aplica ajustes nos itens para igualar (tanto quanto possível) ao total alvo vCents,
 * respeitando granularidade de arredondamento a 0,001 e a capacidade de não ficar negativo.
 *
 * Regras principais:
 *  - Trabalha com "passos efetivos" para respeitar a totalização do relatório.
 *  - Rateia o ajuste em blocos proporcionais ao peso (valor total do item).
 *  - Finaliza com um acabamento fino (passos efetivos unitários).
 *  - Considera MDC dos passos efetivos para detectar casos insolúveis por granularidade.
 *
 * @param itensFiscal Lista de itens fiscais (imutável). Cada item será retornado com `val_icms_st` ou `val_ipi` ajustado.
 * @param vCents      Total alvo (em centavos) que se deseja atingir com o somatório dos itens.
 * @returns           Itens atualizados, residual em centavos e, opcionalmente, o motivo do residual.
 */
export const applyStAdjustment = <T extends { [k: string]: any }>(
  itensFiscal: T[],
  vCents: number,
  key: 'val_icms_st' | 'val_ipi',
): ApplyResult<T> => {
  // Base atual
  const currentTotal = asCents(sumItensCents(itensFiscal, key));
  let diffCents = asCents(vCents - currentTotal);

  // Se o XML não possui o imposto (total = 0), a correção correta é zerar o campo
  if (asCents(vCents) === 0) {
    const updated = itensFiscal.map((it: any) => {
      const q = Number(it?.qtd_entrada ?? 0);
      if (!Number.isFinite(q) || q <= 0) return it;
      return { ...it, [key]: toMoney3(0) };
    });

    return { updated, residualCents: 0 };
  }

  if (diffCents === 0) return { updated: [...itensFiscal], residualCents: 0 };

  const direction = getDirection(diffCents);

  // Lista de trabalho (index/val/qtd) vindo do util
  const work = buildWorkList(itensFiscal, key) as WorkItem[];

  const enriched = enrichWork(work, direction);

  if (enriched.length === 0) {
    return {
      updated: [...itensFiscal],
      residualCents: diffCents,
      reason: 'no_movable_items',
    };
  }

  // Granularidade viável (MDC dos passos efetivos em centavos)
  const g = gcdArray(enriched.map((e) => e.stepCents));
  if (!g) {
    return {
      updated: [...itensFiscal],
      residualCents: diffCents,
      reason: 'no_movable_items',
    };
  }

  // Se diff não for múltiplo do MDC, aproxima para o múltiplo mais próximo
  if (Math.abs(diffCents) % g !== 0) {
    // ajuste alcançável mais próximo a partir do total atual
    const reachableDiff = Math.round(diffCents / g) * g;

    // Se o melhor alcançável for 0, não mexe e devolve residual
    if (reachableDiff === 0) {
      return {
        updated: [...itensFiscal],
        residualCents: diffCents,
        reason: 'unsolvable_granularity',
      };
    }

    diffCents = reachableDiff;
  }

  const minStepCents = Math.min(...enriched.map((e) => e.stepCents));
  if (Math.abs(diffCents) < minStepCents) {
    return {
      updated: [...itensFiscal],
      residualCents: diffCents,
      reason: 'unsolvable_granularity',
    };
  }

  // --- Fase 1: rateio em BLOCOS por passo efetivo ---
  const plannedEffSteps = planEffectiveSteps(enriched, diffCents, direction);
  const { updated: afterBlocks, remained: remainedAfterBlocks } =
    applyPlannedBlocks(enriched, plannedEffSteps, diffCents);

  // --- Fase 2: acabamento fino (passos efetivos unitários) ---
  const { updated: afterFineTune } = fineTuneByEffectiveSteps(
    afterBlocks,
    remainedAfterBlocks,
    direction,
  );

  // Reconstrução: aplica val ajustado ao campo `val_icms_st` ou `val_ipi` preservando o resto do item
  const updated = itensFiscal.map((it, idx) => {
    const w = afterFineTune.find((x) => x.index === idx);
    return w ? ({ ...it, [key]: toMoney3(w.val) } as T) : it;
  });

  const residualCents = asCents(vCents - sumItensCents(updated, key));

  const reason: ResidualReason | undefined =
    residualCents !== 0 &&
    Math.abs(vCents - currentTotal) % Math.max(1, g) !== 0
      ? 'unsolvable_granularity'
      : undefined;

  return { updated, residualCents, reason };
};
